import { useState } from "react";
import { Star, Trash2, Loader2, ShieldCheck, ShieldAlert, Clock, CheckCircle2, Camera, Ban } from "lucide-react";

// Two panels that read the same wherever they appear — a board member's vendor search, a
// manager's vendor sheet, or Redline's directory. Ratings are staff-only: never rendered on any
// vendor- or resident-facing screen.

const Stars = ({ value = 0, size = "h-4 w-4", onPick, testid }) => (
  <span className="inline-flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      onPick ? (
        <button key={n} type="button" data-testid={`${testid}-${n}`} onClick={() => onPick(n)}
          className="transition-transform hover:scale-110" aria-label={`${n} star${n > 1 ? "s" : ""}`}>
          <Star className={`${size} ${n <= value ? "fill-amber-400 text-amber-400" : "text-ink/20"}`} />
        </button>
      ) : (
        <Star key={n} className={`${size} ${n <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-ink/15"}`} />
      )
    ))}
  </span>
);

export const RatingBadge = ({ rating, testid = "rating-badge" }) => {
  if (!rating || !rating.count) return (
    <span data-testid={`${testid}-none`} className="text-xs font-semibold text-ink/35">Not rated yet</span>
  );
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink/70">
      <Stars value={rating.avg} size="h-3.5 w-3.5" />
      <span className="font-bold text-ink">{rating.avg}</span>
      <span className="text-ink/45">
        · {rating.count} rating{rating.count === 1 ? "" : "s"}
        {rating.communities > 1 ? ` · ${rating.communities} communities` : ""}
      </span>
    </span>
  );
};

const Metric = ({ icon: Icon, label, value, suffix = "", tone = "" }) => (
  <div className="rounded-xl border border-sand-200 bg-white px-3.5 py-3">
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-ink/40">
      <Icon className="h-3 w-3" /> {label}
    </div>
    <div className={`mt-1 font-heading text-lg font-extrabold ${tone || "text-ink"}`}>
      {value === null || value === undefined ? "—" : `${value}${suffix}`}
    </div>
  </div>
);

const rateTone = (v) => (v === null || v === undefined ? "" : v >= 90 ? "text-emerald-700" : v >= 70 ? "text-amber-600" : "text-redline-600");

export const VendorPerformance = ({ scorecard, here, testid = "vendor-performance" }) => {
  const [scope, setScope] = useState("network");
  const s = scope === "here" && here ? here : scorecard;
  if (!s) return null;
  return (
    <div data-testid={testid}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">
          What the job record shows · last {Math.round((s.window_days || 180) / 30)} months
        </div>
        {here && (
          <div className="inline-flex rounded-full border border-sand-200 p-0.5 text-[11px] font-bold">
            <button data-testid={`${testid}-scope-network`} onClick={() => setScope("network")}
              className={`rounded-full px-2.5 py-1 transition-colors ${scope === "network" ? "bg-ink text-white" : "text-ink/50"}`}>Everywhere</button>
            <button data-testid={`${testid}-scope-here`} onClick={() => setScope("here")}
              className={`rounded-full px-2.5 py-1 transition-colors ${scope === "here" ? "bg-ink text-white" : "text-ink/50"}`}>Here</button>
          </div>
        )}
      </div>
      {!s.jobs ? (
        <div data-testid={`${testid}-empty`} className="rounded-xl border border-dashed border-sand-300 py-8 text-center text-sm text-ink/45">
          No jobs on record {scope === "here" ? "at this community" : "yet"} — nothing to measure.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Metric icon={CheckCircle2} label="Jobs" value={s.jobs} />
          <Metric icon={ShieldCheck} label="Answered" value={s.accept_rate} suffix="%" tone={rateTone(s.accept_rate)} />
          <Metric icon={Clock} label="Typical reply" value={s.median_response_mins} suffix=" min" />
          <Metric icon={CheckCircle2} label="On time" value={s.on_time_rate} suffix="%" tone={rateTone(s.on_time_rate)} />
          <Metric icon={Camera} label="Left proof" value={s.proof_rate} suffix="%" tone={rateTone(s.proof_rate)} />
          <Metric icon={Ban} label="No answer" value={s.no_response} tone={s.no_response ? "text-redline-600" : ""} />
        </div>
      )}
    </div>
  );
};

export const VendorReviews = ({ reviews = [], rating, canRate, myReview, onRate, onDelete, meId, testid = "vendor-reviews" }) => {
  const [stars, setStars] = useState(myReview?.stars || 0);
  const [note, setNote] = useState(myReview?.note || "");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async () => {
    if (!stars) return;
    setBusy(true);
    await onRate({ stars, note });
    setBusy(false);
    setOpen(false);
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-ink/45">
          What communities say <span className="normal-case font-semibold text-ink/35">· staff only, never shown to the vendor</span>
        </div>
        {canRate && !open && (
          <button data-testid={`${testid}-open-form`} onClick={() => setOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/70 hover:border-ink/30">
            <Star className="h-3.5 w-3.5" /> {myReview ? "Update your rating" : "Rate this vendor"}
          </button>
        )}
      </div>

      {open && (
        <div className="rounded-2xl border border-sand-200 bg-sand-50 p-4" data-testid={`${testid}-form`}>
          <Stars value={stars} onPick={setStars} size="h-6 w-6" testid={`${testid}-pick`} />
          <textarea data-testid={`${testid}-note`} value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="What happened? Turn-up time, quality, how they handled a problem…"
            className="mt-3 w-full resize-none rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          <div className="mt-2 flex items-center gap-2">
            <button data-testid={`${testid}-submit`} onClick={submit} disabled={busy || !stars}
              className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Save
            </button>
            <button data-testid={`${testid}-cancel`} onClick={() => setOpen(false)}
              className="rounded-xl border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
            <span className="text-xs text-ink/40">Other communities will see this.</span>
          </div>
        </div>
      )}

      <RatingBadge rating={rating} testid={`${testid}-summary`} />

      {reviews.length === 0 ? (
        <div data-testid={`${testid}-empty`} className="rounded-xl border border-dashed border-sand-300 py-8 text-center text-sm text-ink/45">
          Nobody has rated them yet.
        </div>
      ) : (
        <div className="space-y-2">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`${testid}-row-${r.id}`} className="rounded-xl border border-sand-200 bg-white p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Stars value={r.stars} size="h-3.5 w-3.5" />
                  <span className="text-xs font-bold text-ink">{r.community}</span>
                  {r.is_board && (
                    <span data-testid={`${testid}-board-tag-${r.id}`} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">Board feedback</span>
                  )}
                  <span className="text-xs text-ink/40">{r.by_name} · {r.by_role}</span>
                </div>
                {(r.by_user_id === meId || onDelete?.force) && onDelete && (
                  <button data-testid={`${testid}-delete-${r.id}`} onClick={() => onDelete(r.id)}
                    className="text-ink/30 transition-colors hover:text-danger-600" aria-label="Remove rating">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {r.note && <p className="mt-1.5 whitespace-pre-wrap text-sm leading-relaxed text-ink/70">{r.note}</p>}
              <div className="mt-1 text-[11px] text-ink/35">
                {(r.created_at || "").slice(0, 10)}{r.action_ref ? ` · job ${r.action_ref}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const InsuredPill = ({ ok, expires, onFile = true }) => {
  // Three states, not two. "Insured" is only honest when we actually hold the certificate —
  // a typed expiry date is a claim, and a board member deserves the same wording Redline sees.
  if (ok && onFile) return (
    <span data-testid="insured-pill-ok" className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
      <ShieldCheck className="h-3.5 w-3.5" /> Insured{expires ? ` thru ${expires}` : ""}
    </span>
  );
  if (expires && !onFile) return (
    <span data-testid="insured-pill-claimed" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
      <ShieldAlert className="h-3.5 w-3.5" /> Expiry claimed, no certificate
    </span>
  );
  return (
    <span data-testid="insured-pill-none" className="inline-flex items-center gap-1 text-xs font-semibold text-redline-600">
      <ShieldAlert className="h-3.5 w-3.5" /> Insurance not current
    </span>
  );
};
